// The Drawing's screen (SURVEY_DRAWING_GRID_C1_WORDS.md, MOCKUP_SURVEY_DRAWING.html, approved 2026-10-30 8:04 AM).
// Step 1, read only: grid paper, room bands, yellow tape labels with their lights, position tabs, Fed From lines with
// their wire tags. Left: what was collected on the label you tapped. Right: the drawing. Pan, pinch and Fit move the
// paper; nothing here changes what feeds what.

import { esc } from "./training-screens";
import { rollUp, type Status } from "./survey-collect";
import { GRID, LABEL_H, portOf, SIDES, TAB_H, TAB_W, type DrawLabel, type Drawing, type DrawLine, type DrawTab, type Side } from "./survey-drawing";

/** Where the paper sits: pan in screen px, zoom. Kept in the page state so a redraw never moves it (item 26). */
export interface DrawView {
  x: number;
  y: number;
  k: number;
}

export interface DrawingScreen {
  d: Drawing;
  view: DrawView;
  /** Locked: the label whose card is open on the left, drawn gold. */
  sel: string | null;
  /**
   * Build is for arranging, Locked is for collecting (BUILD_LOCK_WORDS.md). Build: tray, dots on every label, drag to
   * move and to wire, taps open nothing. Locked: nothing moves, a tap opens the card or the wire card.
   */
  build: boolean;
  /** Build: the line picked, drawn gold with a grip on both ends (item 8). */
  line: string | null;
  /** Locked: the open card's HTML, the same card as Cards (item 13), or null for the hint. */
  card: string | null;
  /** Locked, Loads on: the loads fed from the open card's gear, "H2 · Motor · 25 HP · M-102" (item 9). */
  cardLoads: string[];
  light: (assetId: string) => Status;
  /** The wire tag on a line: its size, or blank (amber) until the wire is filled. */
  tag: (feedId: string) => { text: string; done: boolean };
  tint: (room: string) => string;
  /** The tape name of any asset, tray gear included. */
  name: (assetId: string) => string;
  /** A tray room is open (Robert 2026-10-30 8:19 AM): the room you are in starts open, the rest fold to one line. */
  trayOpen: (room: string) => boolean;
  /** Some label was moved by hand: Arrange shows (item 14). */
  arrange: boolean;
  /** The label held in a drag right now, drawn lifted. */
  dragging: string | null;
  /** A line being drawn from a handle: from the handle to the finger, drawing units (item 18). */
  wire: { x1: number; y1: number; x2: number; y2: number } | null;
  /** The position list for a line drawn from gear with positions (item 19), or null. */
  ask: { title: string; sub: string; points: { key: string; name: string; used: boolean }[] } | null;
  /** Loads switch on (item 9): each label with loads shows how many; the left panel lists them. */
  loads: boolean;
  loadCount: (assetId: string) => number;
}

/** Survey lights (gray, yellow, blue Missing Data, green). The same fills the diagram's corner dots use. */
const FILL: Record<Status, string> = { none: "#9aa1aa", started: "#e0a800", missing: "#2f5fc4", done: "#2ea84f" };

export function drawingScreen(v: DrawingScreen): string {
  // Arrange moves things, so it shows in Build only. Build sits last: Done in its place locks it (items 1 and 10).
  const arrange = v.build && v.arrange ? '<button type="button" class="sv-btn line sm" data-dw="arrange">Arrange</button>' : "";
  const build = v.build ? '<button type="button" class="fl-hdone dw-build" data-dw="build">Done</button>' : '<button type="button" class="sv-btn line sm dw-build" data-dw="build">Build</button>';
  return `<div class="dw-wrap${v.build ? " build" : ""}"><div class="dw-left${v.card && !v.build ? " wide" : ""}" id="dw-left">${leftHtml(v)}</div>
    <div class="dw-main"><div class="dw-tools"><button type="button" class="sv-tog${v.loads ? " on" : ""}" data-dw="loads" aria-pressed="${v.loads}"><i></i>Loads</button><button type="button" class="sv-btn line sm" data-dw="fit">Fit</button>${arrange}${build}</div>
    <div class="dw-paper">${v.build ? trayHtml(v) : ""}<svg id="dw-svg" class="dw-svg" xmlns="http://www.w3.org/2000/svg" aria-label="Drawing">${svgDefs()}<g id="dw-g" transform="${viewTransform(v.view)}">${drawingPaper(v)}</g></svg></div></div></div>${askSheet(v)}`;
}

/** Which way is out of a box on each side. */
const NORMAL: Record<Side, [number, number]> = { Top: [0, -1], Bottom: [0, 1], Left: [-1, 0], Right: [1, 0] };

export const viewTransform = (w: DrawView) => `translate(${round(w.x)} ${round(w.y)}) scale(${round(w.k, 3)})`;
const round = (n: number, p = 1) => Math.round(n * 10 ** p) / 10 ** p;

function svgDefs(): string {
  return `<defs><pattern id="dw-grid" width="${GRID}" height="${GRID}" patternUnits="userSpaceOnUse"><path d="M ${GRID} 0 L 0 0 0 ${GRID}" fill="none" stroke="#e9ecf0" stroke-width="1"/></pattern>
    <filter id="dw-sh" x="-10%" y="-10%" width="130%" height="160%"><feDropShadow dx="0" dy="1.5" stdDeviation="1.2" flood-opacity=".25"/></filter>
    <marker id="dw-arr" viewBox="0 0 10 10" refX="10" refY="5" markerWidth="11" markerHeight="11" markerUnits="userSpaceOnUse" orient="auto"><path d="M0 0 L10 5 L0 10 z" fill="#1d2025"/></marker></defs>`;
}

/** Everything on the paper. A drag redraws only this, once a frame, so its lines follow the finger (item 15). */
export function drawingPaper(v: DrawingScreen): string {
  const d = v.d;
  let s = `<rect x="-20000" y="-20000" width="40000" height="40000" fill="url(#dw-grid)" data-dw="paper"/>`;
  for (const b of d.bands) {
    s += `<rect x="${b.x0}" y="${b.y0}" width="${b.x1 - b.x0}" height="${b.y1 - b.y0}" rx="14" fill="${v.tint(b.room)}" stroke="#d5dbe3" data-dw="paper"/>`;
    s += `<text x="${b.x0 + 12}" y="${b.y0 + 22}" class="dw-room">${esc(b.room)}</text>`;
  }
  // The picked line (Build, item 8): gold under it, the way the picked label is gold.
  const picked = v.build && v.line ? d.lines.find((x) => x.feed.id === v.line) : undefined;
  if (picked) s += `<path d="M${picked.pts.map((p) => `${p.x} ${p.y}`).join(" L")}" class="dw-pick"/>`;
  for (const l of d.lines) s += lineSvg(v, l);
  // The four dots (DRAWING_CONNECTORS_WORDS.md item 1): on every box in Build, the whole time (BUILD_LOCK_WORDS.md
  // item 7). None when Locked. Black and white, never blue. Each takes a finger in a 44 pt zone outside the box, past
  // its tabs, so a press on the box itself always moves it.
  // Dots, grips and their finger zones stay the same size on the screen at any zoom, so a press zoomed out to the whole
  // site still lands on the connector, not the paper. Capped so a zone never spreads over the boxes around it.
  const sc = Math.min(3, 1 / (v.view.k || 1));
  let dotHits = "";
  let dotLooks = "";
  if (v.build || v.wire) {
    for (const l of d.labels) {
      for (const side of SIDES) {
        // The picked line's two ends show their grips there instead (item 8).
        if (picked && ((picked.feed.to === l.assetId && picked.inSide === side) || (picked.feed.from === l.assetId && picked.outSide === side))) continue;
        const dot = dotAt(l, side, d.tabs, sc);
        // The dot sits just outside the edge, so it never covers the name. Small to see, 44 pt to touch: the finger zone
        // around it does the catching (the way draw.io and Visio do it).
        const r = 5.5 * sc;
        const n = NORMAL[side];
        const cx = round(dot.x + n[0] * r);
        const cy = round(dot.y + n[1] * r);
        // The finger zone goes under the labels and the ring on top: a press on a label always lands on that label,
        // even where the zone of the label beside it reaches over (BUILD_LOCK_WORDS.md check, 77-label site).
        dotHits += `<rect x="${dot.hit.x}" y="${dot.hit.y}" width="${dot.hit.w}" height="${dot.hit.h}" rx="${round(12 * sc)}" class="dw-hhit" data-dw="dot" data-dw-v="${esc(l.assetId)}" data-dw-side="${side}"/>`;
        dotLooks += `<g class="dw-dot"><circle cx="${cx}" cy="${cy}" r="${round(r)}" class="dw-hring"/><circle cx="${cx}" cy="${cy}" r="${round(2 * sc)}" class="dw-hdot"/></g>`;
      }
    }
  }
  s += dotHits;
  for (const t of d.tabs) s += `<g class="dw-tab"><rect x="${t.x}" y="${t.y}" width="${TAB_W}" height="${TAB_H}" rx="3"/><text x="${t.x + TAB_W / 2}" y="${t.y + 14}">${esc(t.point)}</text></g>`;
  for (const l of d.labels) {
    const picked = v.sel === l.assetId;
    s += `<g class="dw-label${v.dragging === l.assetId ? " lift" : ""}" data-dw="label" data-dw-v="${esc(l.assetId)}"><title>${esc(l.assetId)}</title>`;
    if (picked) s += `<rect x="${l.x - 5}" y="${l.y - 5}" width="${l.w + 10}" height="${LABEL_H + 10}" rx="7" fill="#f5c400"/>`;
    s += `<rect x="${l.x}" y="${l.y}" width="${l.w}" height="${LABEL_H}" rx="3" class="dw-tape${l.extended ? " ext" : ""}" filter="url(#dw-sh)"/>`;
    s += `<text x="${l.x + l.w / 2}" y="${l.y + 24}" class="dw-name">${esc(l.name)}</text>`;
    s += `<circle cx="${l.x + l.w - 9}" cy="${l.y + 9}" r="4.5" fill="${FILL[v.light(l.assetId)]}" class="dw-light"/>`;
    const n = v.loads ? v.loadCount(l.assetId) : 0;
    // Top left, clear of the wire tag, which sits right of the line coming in.
    if (n) s += `<g class="dw-loads"><rect x="${l.x - 8}" y="${l.y - 21}" width="${n > 9 ? 64 : 56}" height="18" rx="9"/><text x="${l.x}" y="${l.y - 8}">${n} ${n === 1 ? "Load" : "Loads"}</text></g>`;
    s += "</g>";
  }
  s += dotLooks;
  // Grips on both ends of the picked line (DRAWING_CONNECTORS_WORDS.md item 5, BUILD_LOCK_WORDS.md item 8). The end in
  // the fed box: drag it to another side, or to another box. The end leaving the feeder: drag it to another side of the
  // feeder, or to another box to make that box the feeder.
  if (picked && !v.wire) {
    const grip = (p: { x: number; y: number }, side: Side, act: string) => {
      const h = zoneAt(p, side, sc);
      const n = NORMAL[side];
      return `<g class="dw-grip"><circle cx="${round(p.x + n[0] * 5.5 * sc)}" cy="${round(p.y + n[1] * 5.5 * sc)}" r="${round(5.5 * sc)}" class="dw-gring"/><rect x="${h.x}" y="${h.y}" width="${h.w}" height="${h.h}" rx="12" class="dw-hhit" data-dw="${act}" data-dw-v="${esc(picked.feed.id)}"/></g>`;
    };
    s += grip({ x: picked.x2, y: picked.y2 }, picked.inSide, "grip");
    s += grip({ x: picked.x1, y: picked.y1 }, picked.outSide, "grip-out");
  }
  if (v.wire) s += `<path d="M${v.wire.x1} ${v.wire.y1} L${v.wire.x2} ${v.wire.y2}" class="dw-rubber"/>`;
  return s;
}

/**
 * A Fed From line: square corners, dashed when Not Verified, an arrowhead into the box it feeds (DRAWING_CONNECTORS_WORDS
 * item 4), its wire tag on the line, P or E on a transfer switch.
 */
function lineSvg(v: DrawingScreen, l: DrawLine): string {
  const f = l.feed;
  const d = "M" + l.pts.map((p) => `${p.x} ${p.y}`).join(" L");
  const link = esc(f.id);
  let s = `<path d="${d}" class="dw-line${f.notVerified ? " nv" : ""}" marker-end="url(#dw-arr)"/>`;
  // A wide clear stroke takes the finger; a tap opens the same Wire card the cards use (item 23).
  s += `<path d="${d}" class="dw-hit" data-dw="line" data-dw-v="${link}"/>`;
  const t = v.tag(f.id);
  const tw = Math.max(46, t.text.length * 8 + 16);
  // Out the bottom, in the top: the wire tag sits right of the drop, as everywhere. Into a transfer switch, P's letter
  // sits left of its line and E's right of its line with E's wire tag past it (item 21). Any other sides: the tag rides
  // the longest run of the line, right of it when it runs up and down, above it when it runs across.
  let wx: number;
  let ty: number;
  let sx = f.side === "E" ? l.x2 + 10 : l.x2 - 30;
  let sy = l.y2 - 24;
  if (l.outSide === "Bottom" && l.inSide === "Top" && !l.around) {
    ty = Math.round((l.bus + l.y2) / 2);
    wx = f.side === "E" ? sx + 28 : l.x2 + 8;
  } else {
    // The longest stretch where the tag covers no box (Robert 1:20 PM: a tag hid behind SWBD-01).
    const spot = tagSpot(l.pts, tw, v.d);
    wx = spot.x;
    ty = spot.y;
    // The letter sits just outside the box, beside where the line goes in.
    const n = l.pts[l.pts.length - 2] ?? { x: l.x2, y: l.y2 };
    sx = n.x + (l.inSide === "Left" ? -26 : 6);
    sy = n.y + (l.inSide === "Top" ? -22 : 6);
  }
  s += `<g class="dw-wire${t.done ? " done" : ""}" data-dw="line" data-dw-v="${link}"><rect x="${wx}" y="${ty - 11}" width="${tw}" height="22" rx="5"/><text x="${wx + tw / 2}" y="${ty + 4}">${esc(t.done ? t.text : "Wire")}</text></g>`;
  if (f.side) s += `<g class="dw-side"><rect x="${sx}" y="${sy}" width="20" height="18" rx="3"/><text x="${sx + 10}" y="${sy + 13}">${f.side}</text></g>`;
  return s;
}

/** Where a wire tag rides: the longest run of the line where the tag covers no box; else the longest run. */
function tagSpot(pts: readonly { x: number; y: number }[], tw: number, d: Drawing): { x: number; y: number } {
  const boxes = d.labels.map((l) => ({ x0: l.x, y0: l.y, x1: l.x + l.w, y1: l.y + LABEL_H }));
  const runs = pts.slice(1).map((b, i) => ({ a: pts[i]!, b })).sort((p, q) => Math.abs(q.a.x - q.b.x) + Math.abs(q.a.y - q.b.y) - (Math.abs(p.a.x - p.b.x) + Math.abs(p.a.y - p.b.y)));
  const at = (r: { a: { x: number; y: number }; b: { x: number; y: number } }) =>
    r.a.x === r.b.x ? { x: r.a.x + 8, y: Math.round((r.a.y + r.b.y) / 2) } : { x: Math.round((r.a.x + r.b.x) / 2 - tw / 2), y: r.a.y - 16 };
  for (const r of runs) {
    const p = at(r);
    const tag = { x0: p.x, y0: p.y - 11, x1: p.x + tw, y1: p.y + 11 };
    if (!boxes.some((b) => tag.x0 < b.x1 && b.x0 < tag.x1 && tag.y0 < b.y1 && b.y0 < tag.y1)) return p;
  }
  return at(runs[0] ?? { a: pts[0]!, b: pts[0]! });
}

/** The left panel (BUILD_LOCK_WORDS.md items 12, 20 and 21): the hint for each mode, or the open card when Locked. */
function leftHtml(v: DrawingScreen): string {
  if (v.build) {
    return `<div class="fl-hd">Build</div><div class="dw-hint">Drag from Not Connected onto the paper. It stays where you drop it.<br><br>Drag a label to move it. Drag dot to dot to draw a line. Tap a line to move its ends.<br><br>Done locks it.</div>`;
  }
  if (v.card) {
    const loads = v.loads && v.cardLoads.length ? `<div class="fl-hd dw-loadhd">Loads · ${v.cardLoads.length}</div><div class="dw-loadlist">${v.cardLoads.map((x) => `<div>${esc(x)}</div>`).join("")}</div>` : "";
    return `<div class="fl-hd">Card</div>${v.card}${loads}`;
  }
  return `<div class="fl-hd">Drawing</div><div class="dw-hint">Tap a label to open its card. Tap a line for its wire.<br><br>Tap Build to move labels and draw lines.</div>`;
}

/**
 * Not Connected (item 12): gear not on the paper yet, room by room, in hanging order. Build only: drag one onto the
 * paper and it stays where it is dropped (BUILD_LOCK_WORDS.md item 5).
 * Each room folds to one line with its light and count; a tap on the line opens or folds it. The total stays on top.
 */
function trayHtml(v: DrawingScreen): string {
  const n = v.d.tray.reduce((c, r) => c + r.ids.length, 0);
  if (!n) return "";
  return `<div class="dw-tray" id="dw-tray"><div class="fl-hd">Not Connected · ${n}</div>${v.d.tray
    .map((r) => {
      const open = v.trayOpen(r.room);
      const head = `<button type="button" class="dw-trroom${open ? " open" : ""}" data-dw="tray-room" data-dw-v="${esc(r.room)}" aria-expanded="${open}"><i style="background:${FILL[rollUp(r.ids.map(v.light))]}"></i><b>${esc(r.room)}</b><span>${r.ids.length}</span></button>`;
      if (!open) return head;
      return `${head}${r.ids
          .map((id) => `<button type="button" class="dw-trlab${v.sel === id ? " cur" : ""}" data-dw="label" data-dw-v="${esc(id)}"><i style="background:${FILL[v.light(id)]}"></i>${esc(v.name(id))}</button>`)
          .join("")}`;
    })
    .join("")}</div>`;
}

/** A 44 pt finger zone outside a box, on one side, starting at a point on that side's edge. Never on the box. */
export function zoneAt(p: { x: number; y: number }, side: Side, scale = 1): { x: number; y: number; w: number; h: number } {
  const HIT = 44 * scale;
  if (side === "Top") return { x: p.x - HIT / 2, y: p.y - HIT, w: HIT, h: HIT };
  if (side === "Bottom") return { x: p.x - HIT / 2, y: p.y, w: HIT, h: HIT };
  if (side === "Left") return { x: p.x - HIT, y: p.y - HIT / 2, w: HIT, h: HIT };
  return { x: p.x, y: p.y - HIT / 2, w: HIT, h: HIT };
}

/**
 * Where one of a box's four dots sits (DRAWING_CONNECTORS_WORDS.md item 1): the middle of the side, or just past the
 * tabs on that side. Its finger zone starts there and runs outward, so it never covers the box or a tab.
 */
export function dotAt(l: Pick<DrawLabel, "assetId" | "x" | "y" | "w">, side: Side, tabs: readonly DrawTab[], scale = 1): { x: number; y: number; hit: { x: number; y: number; w: number; h: number } } {
  const ts = tabs.filter((t) => t.assetId === l.assetId && t.side === side);
  const p = portOf(l, side);
  let edge = p;
  if (ts.length) {
    if (side === "Bottom") edge = { x: p.x, y: Math.max(...ts.map((t) => t.y + TAB_H)) };
    else if (side === "Top") edge = { x: p.x, y: Math.min(...ts.map((t) => t.y)) };
    else if (side === "Left") edge = { x: Math.min(...ts.map((t) => t.x)), y: p.y };
    else edge = { x: Math.max(...ts.map((t) => t.x + TAB_W)), y: p.y };
  }
  const n = ts.length ? 10 : 0;
  const dot = side === "Top" ? { x: edge.x, y: edge.y - n } : side === "Bottom" ? { x: edge.x, y: edge.y + n } : side === "Left" ? { x: edge.x - n, y: edge.y } : { x: edge.x + n, y: edge.y };
  return { ...dot, hit: zoneAt(edge, side, scale) };
}

/** The position list (item 19): the same list as Fed From on the card. Cancel closes it and nothing changes. */
function askSheet(v: DrawingScreen): string {
  const a = v.ask;
  if (!a) return "";
  const opts = a.points.map((p) => `<button class="mp-opt${p.used ? " used" : ""}" type="button" data-dw="point" data-dw-v="${esc(p.key)}"><b>${esc(p.name)}</b></button>`).join("");
  return `<div class="mp-sheet" data-dw="ask-bg"><div class="mp-card sc-sheet">
    <div class="mp-sheethd"><b>${esc(a.title)}<small>${esc(a.sub)}</small></b><button class="sv-btn line sm" type="button" data-dw="ask-cancel">Cancel</button></div>
    <div class="mp-opts dw-points" id="dw-opts">${opts}</div></div></div>`;
}
